import { useLayoutEffect, useState, type RefObject } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  DEFAULT_PREVIEW_BOARD_COUNT,
  resolvePreviewBoardWidth,
} from './previewBoardSizes';

const MIN_PREVIEW_BOARD_WIDTH = 80;

const previewBoardStackGapPx = (
  isMobile: boolean,
  isWideDesktop: boolean,
  isUltraWide: boolean,
): number => {
  if (isUltraWide) {
    return 12;
  }
  if (isWideDesktop) {
    return 10;
  }
  if (isMobile) {
    return 6;
  }
  return 8;
};

/** Fit preview miniboards to a card/container width (same caps as course and games browse tiles). */
export const useFittedPreviewBoardWidth = (
  containerRef: RefObject<HTMLElement | null>,
  boardCount = DEFAULT_PREVIEW_BOARD_COUNT,
): number => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isUltraWide = useMediaQuery(theme.breakpoints.up('xxxl'));
  const isWideDesktop = useMediaQuery(theme.breakpoints.up('xxl'));
  const maxBoardWidth = resolvePreviewBoardWidth({
    isMobile,
    isWideDesktop,
    isUltraWide,
  });
  const gapPx = previewBoardStackGapPx(isMobile, isWideDesktop, isUltraWide);
  const [boardWidth, setBoardWidth] = useState(maxBoardWidth);

  useLayoutEffect(() => {
    setBoardWidth(maxBoardWidth);
  }, [maxBoardWidth]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const update = () => {
      const containerWidth = container.getBoundingClientRect().width;
      if (containerWidth <= 0) {
        return;
      }
      const fitted = Math.floor(
        (containerWidth - (boardCount - 1) * gapPx) / boardCount,
      );
      setBoardWidth(
        Math.max(MIN_PREVIEW_BOARD_WIDTH, Math.min(maxBoardWidth, fitted)),
      );
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(container);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [boardCount, containerRef, gapPx, maxBoardWidth]);

  return boardWidth;
};
